import { memo } from 'react';
import { Global } from '@emotion/react';
import tw, { GlobalStyles as BaseStyles, css } from 'twin.macro';
import 'react-toastify/dist/ReactToastify.css';

import tailwindConfig from '../../tailwind.config';

/** Latin subset unicode range - excludes CJK characters which fall back to system fonts */
const LATIN_UNICODE_RANGE = `U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD`;

const customStyles = css`
  @font-face {
    font-family: 'Inter-Fallback';
    size-adjust: 104.18%;
    src: local('Arial');
  }

  @font-face {
    font-family: 'Poppins-Fallback';
    size-adjust: 103.25%;
    src: local('Arial');
  }

  /* --- Inter (Variable) --- */
  /* RESTRICTED to Latin: CJK characters use system font fallbacks */
  @font-face {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 100 900;
    font-display: swap;
    src: url('/fonts/inter/inter-v20-latin-opsz-normal.woff2') format('woff2-variations');
    /* ENABLE OPTICAL SIZING (The browser handles this automatically based on font-size) */
    font-optical-sizing: auto;
    unicode-range: ${LATIN_UNICODE_RANGE};
  }

  @font-face {
    font-family: 'Inter';
    font-style: italic;
    font-weight: 400;
    font-display: swap;
    src: url('/fonts/inter/inter-v20-latin-400-italic.woff2') format('woff2');
    unicode-range: ${LATIN_UNICODE_RANGE};
  }

  @font-face {
    font-family: 'Inter';
    font-style: italic;
    font-weight: 700;
    font-display: swap;
    src: url('/fonts/inter/inter-v20-latin-700-italic.woff2') format('woff2');
    unicode-range: ${LATIN_UNICODE_RANGE};
  }

  /* --- Poppins (Static) --- */
  /* RESTRICTED to Latin: CJK characters use system font fallbacks */

  @font-face {
    font-family: 'Poppins';
    font-style: normal;
    font-weight: ${tailwindConfig.theme.fontWeight.regular};
    font-display: swap;
    src: url('/fonts/poppins/poppins-v24-latin-400.woff2') format('woff2');
    unicode-range: ${LATIN_UNICODE_RANGE};
  }

  @font-face {
    font-family: 'Poppins';
    font-style: italic;
    font-weight: ${tailwindConfig.theme.fontWeight.regular};
    font-display: swap;
    src: url('/fonts/poppins/poppins-v24-latin-400-italic.woff2') format('woff2');
    unicode-range: ${LATIN_UNICODE_RANGE};
  }

  @font-face {
    font-family: 'Poppins';
    font-style: normal;
    font-weight: ${tailwindConfig.theme.fontWeight.medium};
    font-display: swap;
    src: url('/fonts/poppins/poppins-v24-latin-500.woff2') format('woff2');
    unicode-range: ${LATIN_UNICODE_RANGE};
  }

  @font-face {
    font-family: 'Poppins';
    font-style: normal;
    font-weight: ${tailwindConfig.theme.fontWeight['semi-bold']};
    font-display: swap;
    src: url('/fonts/poppins/poppins-v24-latin-600.woff2') format('woff2');
    unicode-range: ${LATIN_UNICODE_RANGE};
  }

  @font-face {
    font-family: 'Poppins';
    font-style: italic;
    font-weight: ${tailwindConfig.theme.fontWeight['semi-bold']};
    font-display: swap;
    src: url('/fonts/poppins/poppins-v24-latin-600-italic.woff2') format('woff2');
    unicode-range: ${LATIN_UNICODE_RANGE};
  }

  @font-face {
    font-family: 'Poppins';
    font-style: normal;
    font-weight: ${tailwindConfig.theme.fontWeight.bold};
    font-display: swap;
    src: url('/fonts/poppins/poppins-v24-latin-700.woff2') format('woff2');
    unicode-range: ${LATIN_UNICODE_RANGE};
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
    scroll-padding-top: 80px;
  }

  body {
    ${tw`font-poppins antialiased leading-5`};
    -webkit-tap-highlight-color: transparent;
  }

  * {
    box-sizing: border-box;
  }

  [data-codename][id] {
    scroll-margin-top: 100px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    ${tw`font-poppins`};
  }

  pre,
  code {
    font-family:
      ui-monospace, SFMono-regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
      monospace;
  }

  button {
    background: none;
    border: 0;
    color: inherit;
    font: inherit;
    line-height: normal;
    overflow: visible;
    padding: 0;
  }

  .u-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }

  .u-scroll-forbid {
    inset: 0px;
    height: 100%;
    margin: 0px;
    overflow-y: hidden;
    position: fixed;
  }

  [data-rmiz-btn-unzoom] {
    ${tw`bg-primary-p1`}
  }

  :root {
    --toastify-color-info: ${tailwindConfig.theme.colors.secondary.s1};
    --toastify-color-success: ${tailwindConfig.theme.colors.secondary.s1};
    --toastify-color-error: ${tailwindConfig.theme.colors.primary.p2};
    --toastify-toast-width: 448px;
    --toastify-font-family: 'Inter', sans-serif;
    --toastify-text-color-info: ${tailwindConfig.theme.colors.primary.p1};
    --toastify-text-color-success: ${tailwindConfig.theme.colors.primary.p1};
    --toastify-text-color-warning: ${tailwindConfig.theme.colors.primary.p1};
    --toastify-text-color-error: ${tailwindConfig.theme.colors.primary.p1};
  }
  .Toastify__toast {
    padding: 15px;
  }
  .Toastify__toast-body {
    font-weight: 500;
    padding-bottom: 20px;
  }
  .Toastify__progress-bar {
    bottom: 15px;
    margin: 0 20px;
    width: calc(100% - 40px);
  }
  .Toastify__progress-bar-theme--colored.Toastify__progress-bar--info,
  .Toastify__progress-bar-theme--colored.Toastify__progress-bar--success,
  .Toastify__progress-bar-theme--colored.Toastify__progress-bar--warning,
  .Toastify__progress-bar-theme--colored.Toastify__progress-bar--error {
    ${tw`bg-primary-p1`}
  }
  .Toastify__close-button {
    ${tw`text-primary-p1`}
  }
`;

const GlobalStyles = () => (
  <>
    <BaseStyles />
    <Global styles={customStyles} />
  </>
);

export default memo(GlobalStyles);
